import React from "react";
import { motion } from "framer-motion";
import { Scissors, Ruler, TreePine, Wind, Layers, Sprout, Bug, Leaf, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/useLenis";

const iconMap = { Scissors, Ruler, TreePine, Wind, Layers, Sprout, Bug, Leaf };

export default function Services({ site }) {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative bg-ink-900 py-24 md:py-36"
        >
            <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-14 md:mb-20">
                    <div className="col-span-12 lg:col-span-3">
                        <div className="font-mono-label text-lime-signal">01 — Services</div>
                    </div>
                    <div className="col-span-12 lg:col-span-9">
                        <h2 className="font-display uppercase leading-[0.9] text-5xl md:text-7xl lg:text-8xl">
                            <span className="text-white">Full-service</span>{" "}
                            <span className="text-stroke-white">landscaping,</span>
                            <br />
                            <span className="text-lime-signal">every week.</span>
                        </h2>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                    {site.services.map((s, idx) => {
                        const Icon = iconMap[s.icon] || Leaf;
                        return (
                            <motion.div
                                key={s.id}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, delay: (idx % 4) * 0.06, ease: [0.19, 1, 0.22, 1] }}
                                className="card-tactical group relative overflow-hidden flex flex-col"
                                data-testid={`service-card-${s.id}`}
                            >
                                {s.image && (
                                    <div className="relative aspect-[16/10] overflow-hidden border-b border-white/10">
                                        <img
                                            src={s.image}
                                            alt={s.title}
                                            loading="lazy"
                                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-ink-800/85 via-ink-800/30 to-transparent" />
                                        <span className="absolute top-3 left-3 font-mono-label text-white/80 bg-ink-900/60 backdrop-blur px-2 py-1 rounded-sm">
                                            {String(idx + 1).padStart(2, "0")}
                                        </span>
                                        <ArrowUpRight
                                            size={18}
                                            className="absolute top-3 right-3 text-white/70 group-hover:text-lime-signal transition-colors"
                                        />
                                    </div>
                                )}
                                <div className="p-6 md:p-7 flex-1 flex flex-col">
                                    <Icon className="w-7 h-7 mb-4 text-lime-signal" strokeWidth={1.5} />
                                    <h3 className="font-display uppercase text-2xl md:text-3xl leading-none mb-3">
                                        {s.title}
                                    </h3>
                                    <p className="text-white/60 text-sm leading-relaxed">
                                        {s.desc}
                                    </p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <div className="mt-14 flex flex-wrap items-center justify-between gap-6">
                    <p className="font-mono-label text-white/50 max-w-md">
                        Residential + Commercial · Weekly / Bi-weekly / One-time
                    </p>
                    <button
                        onClick={() => scrollToId("contact")}
                        data-testid="services-cta"
                        className="btn-signal"
                    >
                        Book your walk-through
                        <ArrowUpRight size={18} />
                    </button>
                </div>
            </div>
        </section>
    );
}
